import React, {useMemo, useRef} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {SegmentedControl} from '@tickchak/react-share/app';
import {useVariables} from '../../assets/objects/Variables';
import {DEFAULT_LANG} from '../../globalVariables/locals';
import * as css from './WhatsAppTemplateEditor.css';
import VariableDropdown from '../variableDropdown/VariableDropdown';
import QuillVariablesDropdown from '../textEditor/QuillVariablesDropdown';
import {WHATSAPP_MAX_TEXT_LENGTH} from '../../pages/branding/whatsAppScreen/whatsAppValidation';
import WhatsAppEditorFooter from './components/whatsAppEditorFooter/WhatsAppEditorFooter';
import {DirectionProvider} from './context/DirectionContext';
import {useTemplateStructure} from './hooks/useTemplateStructure';
import {useVariableInsertion} from './hooks/useVariableInsertion';
import {useFormatting} from './hooks/useFormatting';
import {useWhatsAppEditorMode} from './hooks/useWhatsAppEditorMode';
import {useWhatsAppEditorActions} from './hooks/useWhatsAppEditorActions';
import WhatsAppTemplateStructureEditor from './components/whatsappTemplateStructureEditor/WhatsAppTemplateStructureEditor';
import WhatsAppFreeMessageEditor from './components/whatsappFreeMessageEditor/WhatsAppFreeMessageEditor';
import WhatsAppTemplateEditorToolbar from './components/whatsAppTemplateEditorToolbar/WhatsAppTemplateEditorToolbar';
import {TemplateStructure, TextTemplateItem} from './types/templateStructure';
import {TXHR} from '../../assets/constants/constansts.type';

interface WhatsAppTemplateEditorProps {
    events?: {
        onChange?: (structure: TemplateStructure) => void;
        onSave?: (payload: TextTemplateItem[] | string) => void;
    };
    templateStructure: TemplateStructure;
    dependencies?: unknown[];
    'data-cy'?: string;
    variablesType: string;
    modeKey?: string;
    freeMessage: string;
    onFreeMessageChange: (next: string) => void;
    saveXHR?: TXHR;
    toolbarLeadingExtra?: React.ReactNode;
}

function WhatsAppTemplateEditor({
    events = {onChange: undefined, onSave: undefined},
    templateStructure,
    dependencies = [],
    variablesType,
    modeKey,
    freeMessage,
    saveXHR,
    onFreeMessageChange,
    toolbarLeadingExtra = null,
    'data-cy': dataCy,
}: WhatsAppTemplateEditorProps) {
    const {defaultVariables, disabledVariables} = useVariables(variablesType);
    const {t} = useTranslation('variables');
    const {t: tWhatsapp} = useTranslation('whatsapp');
    const currentLang = useSelector((s: {branding?: {currentLang?: string}}) => s.branding?.currentLang || DEFAULT_LANG);
    const eventType = useSelector((s: {event?: {type?: string}}) => s.event?.type || '');
    const direction = currentLang !== 'he' ? 'ltr' : 'rtl';
    const variables = useMemo(() => ({...defaultVariables, ...disabledVariables}), [defaultVariables, disabledVariables]);

    const {
        structure,
        hasChanged: hasTemplateChanged,
        handleItemChange,
        prepareForSave,
        clearEditing,
        clearAllContent,
    } = useTemplateStructure({initialStructure: templateStructure, dependencies, onChange: events.onChange});

    const {
        segmentedValue,
        handleSegmentedChange,
        disabledVariablesWarning,
        totalCharacters,
        filledItemsCount,
        totalItemsCount,
        templateButtonItems,
        isCharCountExceeded,
        hasChanged,
    } = useWhatsAppEditorMode({
        freeMessage,
        structure,
        disabledVariables,
        hasTemplateChanged,
        tWhatsapp,
        modeKey,
    });

    const {handleInsertVariable, setLastFocusedEditable, updateSavedCursorOffset} = useVariableInsertion({
        structure,
        defaultVariables,
        onItemChange: handleItemChange,
    });

    const {handleFormat} = useFormatting({structure, onItemChange: handleItemChange});

    const editorContainerRef = useRef<HTMLDivElement>(null);

    const {freeQuillRef, handleFreeQuillFormat, handleSave, handleClearAll} = useWhatsAppEditorActions({
        events,
        segmentedValue,
        isCharCountExceeded,
        prepareForSave,
        clearEditing,
        freeMessage,
        clearAllContent,
        onFreeMessageChange,
        editorContainerRef,
    });

    return (
        <DirectionProvider direction={direction}>
            <SegmentedControl
                value={segmentedValue}
                onChange={handleSegmentedChange}
                segments={[{id: 'free', label: t('whatsapp:free')}, {id: 'template', label: t('whatsapp:template')}]}
            />
            <css.wrapper ref={editorContainerRef} data-cy={dataCy}>
                <WhatsAppTemplateEditorToolbar
                    totalCharacters={totalCharacters}
                    filledParagraphsCount={filledItemsCount}
                    totalParagraphsCount={totalItemsCount}
                    hasDisabledVariables={disabledVariablesWarning}
                    onClearAll={handleClearAll}
                    clearAllLabel={tWhatsapp('clearAll')}
                    toolbarLeadingExtra={toolbarLeadingExtra}
                    toolbarExtra={
                        <>
                            {segmentedValue === 'template' && (
                                <VariableDropdown
                                    onInsertVariable={handleInsertVariable}
                                    defaultVariables={defaultVariables}
                                    disabledVariables={disabledVariables}
                                    eventType={eventType}
                                    remainingChars={WHATSAPP_MAX_TEXT_LENGTH - totalCharacters}
                                />
                            )}
                            {segmentedValue === 'free' && (
                                <QuillVariablesDropdown
                                    direction={direction}
                                    quillRef={freeQuillRef}
                                    remainingChars={Math.max(0, WHATSAPP_MAX_TEXT_LENGTH - totalCharacters)}
                                    variablesType={variablesType}
                                />
                            )}
                        </>
                    }
                />
                {segmentedValue === 'template' ? (
                    <WhatsAppTemplateStructureEditor
                        structure={structure}
                        handleItemChange={handleItemChange}
                        setLastFocusedEditable={setLastFocusedEditable}
                        updateSavedCursorOffset={updateSavedCursorOffset}
                        variables={variables}
                        t={t}
                    />
                ) : (
                    <WhatsAppFreeMessageEditor
                        quillRef={freeQuillRef}
                        defaultText={freeMessage ?? ''}
                        onChange={onFreeMessageChange}
                        variablesType={variablesType}
                        direction={direction}
                        templateButtonItems={templateButtonItems}
                    />
                )}
                <WhatsAppEditorFooter
                    hasChanged={hasChanged}
                    totalCharacters={totalCharacters}
                    isCharCountExceeded={isCharCountExceeded}
                    save={{
                        onSave: events.onSave ? handleSave : undefined,
                        disabled: isCharCountExceeded || totalCharacters === 0,
                        xhr: saveXHR ?? null,
                        text: segmentedValue === 'template' ? tWhatsapp('saveButton') : tWhatsapp('parseTemplateAndSave')
                    }}
                    onFormat={segmentedValue === 'template' ? handleFormat : handleFreeQuillFormat} 
                />
            </css.wrapper>
        </DirectionProvider>
    );
}

export default WhatsAppTemplateEditor;
